import {Card} from 'primereact/card';
import {Message} from 'primereact/message';
import {Button} from 'primereact/button';
import {Menu} from 'primereact/menu';
import {withTranslation} from 'react-i18next';
import PropTypes from 'prop-types';
import {useEffect, useRef, useState} from 'react';
import DataTableLazy from '../../components/DataTableLazy';
import AuthService from '../../services/AuthService';
import {FaTrophy} from 'react-icons/fa';
import CriteriaCalendarRangeComponent from '../../components/criteria/CriteriaCalendarRangeComponent';
import ImageSimpleComponent from '../../components/inputs/ImageSimpleComponent';
import GroupService from '../../services/GroupService';
import GroupRankingMembersDialog from '../ranking/GroupRankingMembersDialog';
import {t} from 'i18next';
import CompanyService from '../../services/CompanyService';
import {
    getTeamPointsInfoText,
    GROUP_POINTS_ALGORITHM,
    normalizeGroupPointsAlgorithm,
} from '../../utils/GroupPointsAlgorithmUtils';

const GroupRankRowActions = ({rowData, onShowMembers}) => {
    const menuRef = useRef(null);
    const menuItems = [
        {
            label: t('rankingPage.showPointEarnings'),
            icon: 'pi pi-chart-bar',
            command: () => onShowMembers(rowData),
        },
    ];

    return (
        <div>
            <Menu model={menuItems} popup ref={menuRef} />
            <Button icon='pi pi-ellipsis-v' className='menu-items' onClick={(e) => menuRef.current.toggle(e)} />
        </div>
    );
};

GroupRankRowActions.propTypes = {
    rowData: PropTypes.object.isRequired,
    onShowMembers: PropTypes.func.isRequired,
};

export const DashboardGroupRankComponent = ({
    rankingPageable,
    fatchRanking,
    getRankingCleanSearchCriteria,
    rows,
    rowsPerPageOptions,
    groupPointsAlgorithm: groupPointsAlgorithmProp,
}) => {
    const [criteria, setCriteria] = useState(getRankingCleanSearchCriteria());
    const [dateFrom, setDateFrom] = useState(criteria?.dateFrom);
    const [dateTo, setDateTo] = useState(criteria?.dateTo);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [membersDialogVisible, setMembersDialogVisible] = useState(false);
    const [groupPointsAlgorithm, setGroupPointsAlgorithm] = useState(
        normalizeGroupPointsAlgorithm(groupPointsAlgorithmProp)
    );

    useEffect(() => {
        setGroupPointsAlgorithm(normalizeGroupPointsAlgorithm(groupPointsAlgorithmProp));
    }, [groupPointsAlgorithmProp]);

    useEffect(() => {
        if (groupPointsAlgorithmProp) {
            return;
        }
        const companyService = new CompanyService();
        companyService
            .get()
            .then((data) => setGroupPointsAlgorithm(normalizeGroupPointsAlgorithm(data?.groupPointsAlgorithm)))
            .catch(() => setGroupPointsAlgorithm(GROUP_POINTS_ALGORITHM.LOGARITHM));
    }, [groupPointsAlgorithmProp]);
    const groupService = new GroupService();
    const authService = new AuthService();

    useEffect(() => {
        setDateFrom(criteria?.dateFrom);
        setDateTo(criteria?.dateTo);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const rankTemplate = (rowData) => {
        if (rowData.lp === 1) {
            return <FaTrophy className='text-warning' style={{fontSize: '28px'}} />;
        }
        if (rowData.lp === 2) {
            return (
                <b>
                    <div style={{fontSize: '22px', marginLeft: '7px'}}>{rowData.lp}</div>
                </b>
            );
        }
        if (rowData.lp === 3) {
            return (
                <b>
                    <div style={{fontSize: '18px', marginLeft: '8px'}}>{rowData.lp}</div>
                </b>
            );
        }
        return <div style={{fontSize: '16px', marginLeft: '9px'}}>{rowData.lp}</div>;
    };

    const avatarTemplate = (rowData) => {
        const groupId = rowData?.groupId;
        if (!groupId) {
            return '';
        }
        const avatarPath = `${groupService.getPath()}/${groupId}/avatar`;
        return (
            <ImageSimpleComponent
                srcUrl={`${avatarPath}?token=${authService.getToken()}`}
                borderRadius={50}
                height={40}
                width={40}
            />
        );
    };

    const cleanPaginationCriteria = (nextCriteria) => {
        nextCriteria.firstResult = 0;
        nextCriteria.maxResults = rows;
    };

    const openMembersDialog = (rowData) => {
        setSelectedGroup({groupId: rowData.groupId, name: rowData.name});
        setMembersDialogVisible(true);
    };

    const actionsTemplate = (rowData) => (
        <GroupRankRowActions rowData={rowData} onShowMembers={openMembersDialog} />
    );

    return (
        <div>
            <Card className='dashboard-card-rank mt-1'>
                <Message
                    severity='info'
                    className='w-100 mb-3'
                    text={getTeamPointsInfoText(t, groupPointsAlgorithm)}
                />
                <div className='row'>
                    <div className='col-lg-4 offset-lg-8'>
                        <CriteriaCalendarRangeComponent
                            idFrom='group-rank-date-from'
                            idTo='group-rank-date-to'
                            nameFrom='dateFrom'
                            nameTo='dateTo'
                            from={dateFrom}
                            to={dateTo}
                            label={'Zakres'}
                            onChange={(type, names, e) => {
                                const value = e?.value;
                                const name = e?.target?.name;
                                const nextCriteria = {...criteria};
                                if (name === 'dateFrom') {
                                    setDateFrom(value);
                                    nextCriteria.dateFrom = value;
                                } else if (name === 'dateTo') {
                                    setDateTo(value);
                                    nextCriteria.dateTo = value;
                                }
                                cleanPaginationCriteria(nextCriteria);
                                setCriteria(nextCriteria);
                                fatchRanking(nextCriteria);
                            }}
                            sharedLabel={true}
                            inputsClassName='col-md-6'
                            colClass='col-lg-12'
                            showButtonBar={true}
                        />
                    </div>
                </div>

                <DataTableLazy
                    id='group-rank-table'
                    dataPageable={rankingPageable}
                    rendered={true}
                    filterDisplay={'menu'}
                    fetchReturnResolve={false}
                    rows={rows}
                    loadLazyData={(nextCriteria) => {
                        fatchRanking(nextCriteria);
                    }}
                    paginator={true}
                    onSelect={() => {}}
                    rowsPerPageOptions={rowsPerPageOptions || []}
                    initialCriteria={criteria}
                    columns={[
                        {field: '', header: t('dashboard.place'), body: rankTemplate},
                        {field: 'avatarUuid', header: t('groups.avatar'), body: avatarTemplate},
                        {field: 'name', header: t('groups.groupName')},
                        {
                            field: 'membersCount',
                            header: t('rankingPage.membersCount'),
                            bodyStyle: {textAlign: 'center', fontVariantNumeric: 'tabular-nums', fontFeatureSettings: '"tnum"'},
                            style: {width: '100px'},
                        },
                        {
                            field: 'earnedTotal',
                            header: t('dashboard.points'),
                            bodyStyle: {textAlign: 'center', fontVariantNumeric: 'tabular-nums', fontFeatureSettings: '"tnum"'},
                            style: {width: '120px'},
                        },
                        {
                            field: 'actions',
                            header: t('list.actions'),
                            body: actionsTemplate,
                            bodyStyle: {textAlign: 'center'},
                            style: {width: '80px'},
                        },
                    ]}
                />
            </Card>
            <GroupRankingMembersDialog
                visible={membersDialogVisible}
                group={selectedGroup}
                initialDateFrom={dateFrom}
                initialDateTo={dateTo}
                rows={rows}
                rowsPerPageOptions={rowsPerPageOptions}
                groupPointsAlgorithm={groupPointsAlgorithm}
                onHide={() => {
                    setMembersDialogVisible(false);
                    setSelectedGroup(null);
                }}
            />
        </div>
    );
};

DashboardGroupRankComponent.propTypes = {
    rankingPageable: PropTypes.object,
    getRankingCleanSearchCriteria: PropTypes.func.isRequired,
    fatchRanking: PropTypes.func.isRequired,
    rows: PropTypes.number,
    rowsPerPageOptions: PropTypes.array,
    groupPointsAlgorithm: PropTypes.string,
};

export default withTranslation()(DashboardGroupRankComponent);
